import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject, takeUntil } from 'rxjs';
import { CallType } from '../../../../core/models/group-call/group-call.model';

export interface MediaConsentResult {
  audioAllowed: boolean;
  videoAllowed: boolean;
}

@Component({
  selector: 'app-media-consent-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './media-consent-modal.component.html',
  styleUrl: './media-consent-modal.component.scss',
})
export class MediaConsentModalComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() visible: boolean = false;
  @Input() callType: CallType | string | number = CallType.Video;
  @Input() forceShow: boolean = true;
  @Input() initialAudioAllowed: boolean = false;
  @Input() initialVideoAllowed: boolean = false;

  @Output() confirmed = new EventEmitter<MediaConsentResult>();
  @Output() cancelled = new EventEmitter<void>();

  audioConsent: boolean = false;
  videoConsent: boolean = false;
  consentStep = 0; // 0: Audio, 1: Video

  audioHtmlSafe: SafeHtml = '';
  videoHtmlSafe: SafeHtml = '';

  private readonly destroyed$ = new Subject<void>();

  constructor(
    private translate: TranslateService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.translate
      .stream('auth.login.audioConsentLongHtml')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((text) => {
        this.audioHtmlSafe = this.sanitizer.bypassSecurityTrustHtml(text);
      });

    this.translate
      .stream('auth.login.videoConsentLongHtml')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((text) => {
        this.videoHtmlSafe = this.sanitizer.bypassSecurityTrustHtml(text);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue) {
      this.applyInitialState(true);
      return;
    }

    if (changes['initialAudioAllowed'] || changes['initialVideoAllowed']) {
      this.applyInitialState(false);
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  nextConsentStep() {
    if (!this.isVideoCall()) {
      this.confirm();
      return;
    }
    if (this.consentStep < 1) {
      this.consentStep += 1;
    }
  }

  prevConsentStep() {
    if (this.consentStep > 0) {
      this.consentStep -= 1;
    }
  }

  setAudioConsent(value: boolean) {
    this.audioConsent = value;
  }

  setVideoConsent(value: boolean) {
    this.videoConsent = value;
  }

  confirm() {
    this.confirmed.emit({
      audioAllowed: this.audioConsent,
      videoAllowed: this.isVideoCall()
        ? this.videoConsent
        : this.initialVideoAllowed,
    });
  }

  cancel() {
    this.cancelled.emit();
  }

  applyInitialState(resetStep: boolean): void {
    this.audioConsent = this.initialAudioAllowed;
    this.videoConsent = this.initialVideoAllowed;
    if (resetStep) {
      this.consentStep = 0;
    }
  }

  isVideoCall(): boolean {
    if (typeof this.callType === 'number') {
      return this.callType === CallType.Video;
    }
    const normalized = String(this.callType || '').toLowerCase();
    return normalized === 'video' || normalized === '2';
  }
}
